import {downloadFile, get, post, uploadFile} from "../utils/networkUtils";
import {COURSE_DOCUMENT_FILE_UPLOAD_POST_KEY, COURSE_REVIEW_FILE_UPLOAD_POST_KEY} from "../constants";

/**
 * Gets every course in the conf (including draft and archived ones) for the admin screens
 */
export function getCourseList(onComplete) {
  get("course/list/", onComplete);
}

/**
 * Creates or updates a course
 */
export function upsertCourse(courseId, title, subtitle, description, courseStatus, imageUrl, courseOrder,
                             onComplete) {
  const postData = {
    id: courseId,
    title: title,
    subtitle: subtitle,
    description: description,
    course_status: courseStatus,
    image_url: imageUrl,
    course_order: courseOrder,
  };
  post("course/upsert/", postData, onComplete);
}

/**
 * Deletes a course.  This only works while nobody has bought it or has access to it.
 */
export function deleteCourse(courseId, onComplete) {
  const postData = {
    id: courseId,
  };
  post("course/delete/", postData, onComplete);
}

/**
 * Gets the pages in one course, in the order they are shown
 */
export function getCoursePageList(courseId, onComplete) {
  get(`course/${courseId}/page/list/`, onComplete);
}

/**
 * Creates or updates a page of course content.  A new page is added to the end of the course.
 * Every page belongs to one course - the courseId is only used when creating a page.
 */
export function upsertCoursePage(coursePageId, courseId, title, html, pageOrder, onComplete) {
  const postData = {
    id: coursePageId,
    course_id: courseId,
    title: title,
    html: html,
    page_order: pageOrder,
  };
  post("course/page/upsert/", postData, onComplete);
}

/**
 * Deletes a page from its course
 */
export function deleteCoursePage(coursePageId, onComplete) {
  const postData = {
    id: coursePageId,
  };
  post("course/page/delete/", postData, onComplete);
}

/**
 * Gets the instructors assigned to one course
 */
export function getCourseInstructorList(courseId, onComplete) {
  get(`course/${courseId}/instructor/list/`, onComplete);
}

/**
 * Gets everyone who currently has access to one course, one row per user
 */
export function getCourseStudentList(courseId, onComplete) {
  get(`course/${courseId}/student/list/`, onComplete);
}

/**
 * Makes a user an instructor on a course, which lets them edit it.
 * Only a course admin (or conf admin / staff) can do this.  Returns the updated instructor list.
 */
export function addCourseInstructor(courseId, userId, onComplete) {
  const postData = {
    course_id: courseId,
    userid: userId,
  };
  post("course/instructor/add/", postData, onComplete);
}

/**
 * Takes an instructor off a course.  Returns the updated instructor list.
 */
export function removeCourseInstructor(courseId, userId, onComplete) {
  const postData = {
    course_id: courseId,
    userid: userId,
  };
  post("course/instructor/remove/", postData, onComplete);
}

/**
 * Gets the documents in one course
 */
export function getCourseDocumentList(courseId, onComplete) {
  get(`course/${courseId}/document/list/`, onComplete);
}

/**
 * Uploads a document to a course (into the private file upload bucket).  Returns the updated document list.
 */
export function uploadCourseDocument(courseId, title, docKey, file, onComplete) {
  const postData = {
    course_id: courseId,
    title: title,
    doc_key: docKey,
  };
  uploadFile("course/document/upload/", COURSE_DOCUMENT_FILE_UPLOAD_POST_KEY, file, postData, onComplete);
}

/**
 * Changes a course document's title and key.  When file is given it also replaces the document's file.
 * Returns the updated document list.
 */
export function updateCourseDocument(courseDocumentId, title, docKey, file, onComplete) {
  const postData = {
    id: courseDocumentId,
    title: title,
    doc_key: docKey,
  };
  if (file) {
    uploadFile("course/document/update/", COURSE_DOCUMENT_FILE_UPLOAD_POST_KEY, file, postData, onComplete);
  } else {
    post("course/document/update/", postData, onComplete);
  }
}

/**
 * Deletes a course document and its file.  Returns the updated document list.
 */
export function deleteCourseDocument(courseDocumentId, onComplete) {
  post("course/document/delete/", {id: courseDocumentId}, onComplete);
}

/**
 * Gets the videos in one course
 */
export function getCourseVideoList(courseId, onComplete) {
  get(`course/${courseId}/video/list/`, onComplete);
}

/**
 * Adds a video (already uploaded to MUX) to a course, or updates one when courseVideoId is given.
 * The playback id, status, duration and thumbnail are re-read from MUX on every save.  Returns the updated video list.
 */
export function upsertCourseVideo(courseVideoId, courseId, title, description, videoKey, muxAssetId, onComplete) {
  const postData = {
    id: courseVideoId,
    course_id: courseId,
    title: title,
    description: description,
    video_key: videoKey,
    mux_asset_id: muxAssetId,
  };
  post("course/video/upsert/", postData, onComplete);
}

/**
 * Removes a video from a course (the asset at MUX is kept).  Returns the updated video list.
 */
export function deleteCourseVideo(courseVideoId, onComplete) {
  post("course/video/delete/", {id: courseVideoId}, onComplete);
}

/**
 * Gets the price options of one course, in the order they are shown
 */
export function getCoursePriceList(courseId, onComplete) {
  get(`course/${courseId}/price/list/`, onComplete);
}

/**
 * Adds a price option to a course, or updates one when coursePriceId is given.  amount is in cents (0 is free).
 * courseAccessType (constants COURSE_ACCESS_TYPE_*) says how long the access bought at this price lasts.
 * Returns the updated price list.
 */
export function upsertCoursePrice(coursePriceId, courseId, name, description, amount, courseAccessType,
                                  accessDays, accessEndDate, startDate, endDate, requiresReview, requiresFile,
                                  reviewInstructions, active, priceOrder, onComplete) {
  const postData = {
    id: coursePriceId,
    course_id: courseId,
    name: name,
    description: description,
    amount: amount,
    course_access_type: courseAccessType,
    access_days: accessDays,
    access_end_date: accessEndDate,
    start_date: startDate,
    end_date: endDate,
    requires_review: requiresReview,
    requires_file: requiresFile,
    review_instructions: reviewInstructions,
    active: active,
    price_order: priceOrder,
  };
  post("course/price/upsert/", postData, onComplete);
}

/**
 * Deletes a price option (refused once it is on an order).  Returns the updated price list.
 */
export function deleteCoursePrice(coursePriceId, onComplete) {
  post("course/price/delete/", {id: coursePriceId}, onComplete);
}

/**
 * Gets every course coupon in the conf (course admins only)
 */
export function getCourseCouponList(onComplete) {
  get("course/coupon/list/", onComplete);
}

/**
 * Adds a course coupon, or updates one when courseCouponId is given.  Give discountPercent or discountAmount (cents),
 * the other null.  courseId limits it to one course (null for any).  allowedUsers is a list of users ({id, ...}),
 * empty for anyone.  Returns the updated coupon list.
 */
export function upsertCourseCoupon(courseCouponId, code, description, courseId, discountPercent, discountAmount,
                                   totalCount, unlimitedCount, allowedUsers, startDate, endDate, active, onComplete) {
  const postData = {
    id: courseCouponId,
    code: code,
    description: description,
    course_id: courseId,
    discount_percent: discountPercent,
    discount_amount: discountAmount,
    total_count: totalCount,
    unlimited_count: unlimitedCount,
    allowed_users: allowedUsers,
    start_date: startDate,
    end_date: endDate,
    active: active,
  };
  post("course/coupon/upsert/", postData, onComplete);
}

/**
 * Deletes a course coupon (refused once it is on an order).  Returns the updated coupon list.
 */
export function deleteCourseCoupon(courseCouponId, onComplete) {
  post("course/coupon/delete/", {id: courseCouponId}, onComplete);
}

/**
 * Checks a whole course (pages, documents and their files, instructors).
 * Returns a list of {severity, message, tab} - see courses_utils.validate_course()
 */
export function getCourseValidation(courseId, onComplete) {
  get(`course/${courseId}/validate/`, onComplete);
}

/**
 * Gets the course catalog - the published courses with a price which can be bought today, and those prices
 */
export function getCourseCatalog(onComplete) {
  get("course/catalog/", onComplete);
}

/**
 * Gets one course for its own page - the catalog course plus can_view_content and its list of pages (titles only)
 */
export function getCourseCatalogCourse(courseId, onComplete) {
  get(`course/catalog/${courseId}/`, onComplete);
}

/**
 * Gets the user's cart - {id, amount, items, coupon}.  An empty cart (id null) when they don't have one.
 */
export function getCourseCart(onComplete) {
  get("course/cart/", onComplete);
}

/**
 * Puts a course in the cart at a price (or changes the price when the course is already in the cart).
 * A free price which needs no review can't go in the cart - use enrollInCourse.  Returns the cart.
 */
export function addToCourseCart(coursePriceId, onComplete) {
  post("course/cart/add/", {course_price_id: coursePriceId}, onComplete);
}

/**
 * Takes one line out of the cart.  Returns the cart.
 */
export function removeFromCourseCart(cartItemId, onComplete) {
  post("course/cart/remove/", {id: cartItemId}, onComplete);
}

/**
 * Saves the note for the reviewer on a cart line whose price needs review.  When file is given it is uploaded as the
 * proof (replacing any earlier one).  Returns the cart.
 */
export function saveCourseCartReview(cartItemId, reviewNote, file, onComplete) {
  const postData = {
    id: cartItemId,
    review_note: reviewNote || '',
  };
  if (file) {
    uploadFile("course/cart/review/", COURSE_REVIEW_FILE_UPLOAD_POST_KEY, file, postData, onComplete);
  } else {
    post("course/cart/review/", postData, onComplete);
  }
}

/**
 * Applies a coupon code to the cart (replacing the coupon already on it).  Returns the cart.
 */
export function applyCourseCartCoupon(code, onComplete) {
  post("course/cart/coupon/apply/", {code: code}, onComplete);
}

/**
 * Takes the coupon off the cart.  Returns the cart.
 */
export function removeCourseCartCoupon(onComplete) {
  post("course/cart/coupon/remove/", {}, onComplete);
}

/**
 * Checks out the cart.  amount is the total the user was shown - checkout is refused if it has changed.
 * Returns {course_order_id, amount, stripe_client_secret, completed}: confirm the payment with the client secret,
 * or when completed is set (a zero total) the order is already done.
 */
export function checkoutCourseCart(amount, onComplete) {
  post("course/checkout/", {amount: amount}, onComplete);
}

/**
 * Gets one of the user's orders.  If it is still waiting on its payment the server asks Stripe how it went.
 */
export function getCourseOrder(courseOrderId, onComplete) {
  get(`course/order/${courseOrderId}/`, onComplete);
}

/**
 * Enrolls the user in a course at a free price which needs no review - access is granted straight away
 */
export function enrollInCourse(coursePriceId, onComplete) {
  post("course/enroll/", {course_price_id: coursePriceId}, onComplete);
}

/**
 * Gets the courses the logged in user can study - the ones they have access to, plus the ones they teach
 */
export function getMyCourseList(onComplete) {
  get("course/my/list/", onComplete);
}

/**
 * Opens a course for studying - returns {course, pages}.  The pages have no content, load each with getMyCoursePage.
 */
export function getMyCourseOutline(courseId, onComplete) {
  get(`course/my/${courseId}/outline/`, onComplete);
}

/**
 * Gets the content of one course page for studying
 */
export function getMyCoursePage(coursePageId, onComplete) {
  get(`course/my/page/${coursePageId}/`, onComplete);
}

/**
 * Gets what the player needs to play one course video, including short-lived MUX playback tokens.
 * The tokens expire, so call this each time the player is shown rather than caching the result.
 */
export function getMyCourseVideo(courseVideoId, onComplete) {
  get(`course/my/video/${courseVideoId}/`, onComplete);
}

/**
 * Downloads a course document - this is what the {doc:key} links in a course page do
 */
export function downloadCourseDocument(courseDocumentId, fileName) {
  downloadFile(`course/my/document/${courseDocumentId}/download/`, fileName);
}

/**
 * Gets the purchases which need review (waiting and already decided) for the courses the user may review
 */
export function getCourseReviewList(onComplete) {
  get("course/review/list/", onComplete);
}

/**
 * Approves (granting the access) or denies a purchase which needs review.  Returns the updated review list.
 */
export function decideCourseReview(courseOrderItemId, approve, reviewReason, onComplete) {
  const postData = {
    id: courseOrderItemId,
    approve: approve,
    review_reason: reviewReason,
  };
  post("course/review/decide/", postData, onComplete);
}

/**
 * Downloads the proof a buyer uploaded with a purchase which needs review
 */
export function downloadCourseReviewFile(courseOrderItemId, fileName) {
  downloadFile(`course/review/${courseOrderItemId}/file/`, fileName);
}

/**
 * Downloads the Sales Report - every course order, its lines and the free enrollments (course admins only)
 */
export function downloadCourseSalesReport() {
  downloadFile('course/report/sales/excel/', 'course_sales.xlsx');
}

/**
 * Downloads the Course Usage Report - the pages, documents and videos each student viewed (course admins only)
 */
export function downloadCourseUsageReport() {
  downloadFile('course/report/usage/excel/', 'course_usage.xlsx');
}

/**
 * Gets a page of the CourseHistory log for the Tracking screen (staff only)
 */
export function getCourseHistoryAll(showAllConf, filterUserId, dateFilterStart, dateFilterEnd, courseId, action,
                                    userSearch, pageNum, pageSize, onComplete) {
  const data = {
    'show_all_conf': showAllConf,
    'filter_user_id': filterUserId,
    'date_filter_start': dateFilterStart,
    'date_filter_end': dateFilterEnd,
    'course_id': courseId,
    'action': action,
    'user_search': userSearch,
    'page_num': pageNum,
    'page_size': pageSize,
  };
  post('course/history/all/', data, onComplete);
}

/**
 * Gets all the CourseHistory actions used (staff only)
 */
export function getCourseHistoryActions(onComplete) {
  get('course/history/actions/', onComplete);
}

/**
 * Gets the course email templates (course admins only)
 */
export function getCourseEmails(onComplete) {
  get('course/email/list/', onComplete);
}

/**
 * Saves one of the course email templates (course admins only).  Returns all the course email templates.
 */
export function updateCourseEmail(key, subject, bodyHtml, onComplete) {
  const data = {
    key: key,
    subject: subject,
    body_html: bodyHtml,
  };
  post('course/email/update/', data, onComplete);
}
